import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  SUPPORTED_LOCALES,
  translate,
  formatCurrency,
  formatDate,
  type Locale,
} from "@isale/i18n";

interface I18nContextValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  /**
   * F-03 — adopt the user's account locale (from /users/me) when they haven't
   * explicitly picked a locale on this device. Keeps web consistent with the
   * locale the user chose on mobile instead of defaulting to navigator.language.
   */
  syncFromAccount: (l: Locale) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
  formatCurrency: (value: number) => string;
  formatDate: (date: Date | string, opts?: Intl.DateTimeFormatOptions) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

const STORAGE_KEY = "isale.locale";

function isLocale(v: string | null): v is Locale {
  return typeof v === "string" && (SUPPORTED_LOCALES as readonly string[]).includes(v);
}

function detectInitialLocale(): Locale {
  if (typeof window === "undefined") return "vi";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (isLocale(stored)) return stored;
  // Hint from navigator.language — match by 2-letter prefix
  const code = (navigator.language ?? "vi").slice(0, 2).toLowerCase();
  if (code === "en" || code === "zh") return code;
  return "vi";
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(detectInitialLocale);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try {
      window.localStorage.setItem(STORAGE_KEY, l);
    } catch {
      // best-effort
    }
  }, []);

  const syncFromAccount = useCallback((l: Locale) => {
    // Only when the user hasn't explicitly chosen a locale on this device
    // (STORAGE_KEY unset). State-only (NOT persisted) so a later account-locale
    // change on another surface still propagates on next load. An explicit web
    // choice (setLocale → STORAGE_KEY set) always wins over this.
    if (window.localStorage.getItem(STORAGE_KEY)) return;
    setLocaleState((cur) => (cur === l ? cur : l));
  }, []);

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) =>
      translate(locale, key, vars),
    [locale],
  );

  return (
    <I18nContext.Provider
      value={{
        locale,
        setLocale,
        syncFromAccount,
        t,
        formatCurrency: (v) => formatCurrency(v, locale),
        formatDate: (d, opts) => formatDate(d, locale, opts),
      }}
    >
      {children}
    </I18nContext.Provider>
  );
}

export function useTranslation(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error("useTranslation() must be used inside <I18nProvider />.");
  }
  return ctx;
}
